import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Seconds ticking down to zero — used for the "resend code" cooldown.
 *
 * `start(seconds)` (re)arms it; the interval is torn down on unmount and whenever
 * it reaches zero, so an abandoned screen never keeps a timer alive.
 */
export function useCountdown(initialSeconds = 0) {
  const [seconds, setSeconds] = useState(initialSeconds)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(
    (nextSeconds: number) => {
      clear()
      setSeconds(Math.max(0, Math.ceil(nextSeconds)))
      if (nextSeconds <= 0) return
      timerRef.current = setInterval(() => {
        setSeconds((current) => {
          if (current <= 1) {
            clear()
            return 0
          }
          return current - 1
        })
      }, 1000)
    },
    [clear],
  )

  useEffect(() => clear, [clear])

  return { seconds, start, isCounting: seconds > 0 }
}
